import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { uniqWith, isEqual, find as _find, reject, filter } from 'lodash';
import { DrawingManager } from '@ngui/map';
import {
  combineLatest as observableCombineLatest, Subscription
} from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';

import { LocationService } from './location.service';
import { AuthenticationService } from './../shared/authentication.service';
import { ApiService, parseErrors } from '../shared/api.service';
import { mapOptions } from '../shared/static-data';
import { LocationOwnerService } from './location-owner.service';
import { Location } from './location';
import { LocationSerializer } from './location.serializer';
import { LocationTypeService } from './location-type.service';
import { DropdownComponent } from '../shared';
import { Address } from './address';
import { LocationOwnerSerializer } from './location-owner.serializer';

declare let google: any;
declare let navigator: any;

@Component({
  selector: 'ruckit-edit-location',
  templateUrl: './edit-location.component.html',
  styleUrls: ['./edit-location.component.scss']
})
export class EditLocationComponent implements OnInit {
  loading = false;
  showLocationOwnerTextField = false;
  location: Location = new LocationSerializer().fromJson({
    latitude: 29.9291564, longitude: -94.9320884, geofence: null
  });
  errors = [];
  states = [];
  stateOptions = [];
  countryOptions = [];
  ownerOptions = [];
  ownersConfig = {
    nameProperty: 'name',
    searchable: true,
    loadingOptions: false
  };
  zoom = 8;
  callback: Function;
  drawingMode = undefined;
  polygon;
  paths = [];
  returnTo: string;
  marker;
  geolocationSub;
  device = {
    info: null,
    mobile: false,
    tablet: false,
    desktop: false
  };
  canGeolocate = false;
  address: Address = new Address();
  selectedAddress: Address = new Address();
  addresses: Address[] = [];
  showingAddresses: boolean;
  selectedOverlay: any;
  mapOptions = {};
  qrEnabled = false;
  laserFicheEnabled = false;
  locationReq: Subscription;
  locationOwnersReq: Subscription;
  @ViewChild(DrawingManager, { static: false }) drawingManager: DrawingManager;
  @ViewChild('ownersDropdown', { static: true }) ownersDropdown: DropdownComponent;
  map: google.maps.Map;
  markerLocation: Location = new Location();
  markerMoved = false;
  isGeoFenceAvailable = false;

  locationTypeDropdownConfig = {
    service: LocationTypeService,
    selectText: 'Select Location Type',
    loadingText: 'Loading Location Types...',
    noResultsText: 'No Location Types'
  };
  allSubscriptionsToUnsubscribe: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private locationService: LocationService,
    private apiService: ApiService,
    private authenticationService: AuthenticationService,
    private locationOwnerService: LocationOwnerService,
    private deviceDetectorService: DeviceDetectorService
  ) {
    this.device = {
      info: this.deviceDetectorService.getDeviceInfo(),
      mobile: this.deviceDetectorService.isMobile(),
      tablet: this.deviceDetectorService.isTablet(),
      desktop: this.deviceDetectorService.isDesktop()
    };
    if (navigator && navigator.geolocation) {
      this.canGeolocate = true;
    }
  }

  ngOnInit() {
    this.states = this.apiService.getStates();
    this.countryOptions = this.apiService.getCountries().map(country => {
      return {
        id: country.abbreviation,
        name: country.name
      };
    });
    if (this.location && this.location.country) {
      this.selectCountry(this.location.country);
    } else {
      this.selectCountry('US');
    }

    const user = this.authenticationService.user();
    this.qrEnabled = user && user.organization && user.organization.qrEnabled;
    const enabledFeatures = this.authenticationService.enabledFeatures();
    if (enabledFeatures && enabledFeatures.includes('useLaserficheExport')) {
      this.laserFicheEnabled = true;
    }

    this.getLocationOwners();


    let combinedParams = observableCombineLatest(
      this.route.params, this.route.queryParams,
      (params, qparams) => ({ params, qparams })
    );

    this.allSubscriptionsToUnsubscribe.push(
      combinedParams.subscribe(result => {
        this.returnTo = result.qparams['returnTo'];
        if (result && result.params['id']) {
          this.getLocation(result.params['id']);
        } else {
          this.router.navigate([this.returnTo]);
        }
      })
    );
  }

  ngOnDestroy() {
    if (this.locationOwnersReq && typeof this.locationOwnersReq.unsubscribe === 'function') {
      this.locationOwnersReq.unsubscribe();
    }
    if (this.locationReq && typeof this.locationReq.unsubscribe === 'function') {
      this.locationReq.unsubscribe();
    }
    this.allSubscriptionsToUnsubscribe.forEach(sub => {
      sub.unsubscribe();
    });
  }

  getLocation(id: string): void {
    if (this.locationReq && typeof this.locationReq.unsubscribe === 'function') {
      this.locationReq.unsubscribe();
    }
    if (id) {
      this.loading = true;
      this.locationReq = this.locationService.get(id).subscribe(location => {
        this.location = location;
        this.selectCountry(this.location.country);
        this.selectState(this.location.state);
        if (this.location.geofence) {
          this.isGeoFenceAvailable = true;
          this.paths = this.location.paths || [];
          if (this.map) { this.initDrawingManager(); }
        }
        let options = {
          scrollwheel: true,
          mapTypeId: 'hybrid',
          zoom: 17,
          mapTypeControlOptions: {
            mapTypeIds: ['roadmap', 'hybrid']
          }
        };
        if (this.location && this.location.latitude && this.location.longitude) {
          this.marker = {
            lat: this.location.latitude,
            lon: this.location.longitude,
            mappable: this.location.mappable
          };
          options['center'] = {
            lat: parseFloat(this.location.latitude),
            lng: parseFloat(this.location.longitude)
          };
        }
        this.mapOptions = mapOptions({
          zoom: 15,
          disableDefaultUI: true,
          scrollwheel: true,
          fullscreenControl: false,
          streetViewControl: false
        }, {}, {
          mapStyle: 'google-map-style'
        });
        this.loading = false;
      }, err => {
        this.errors = err;
        this.loading = false;
      });
    }
  }

  getLocationOwners(query = {}) {
    if (this.locationOwnersReq && typeof this.locationOwnersReq.unsubscribe === 'function') {
      this.locationOwnersReq.unsubscribe();
    }

    this.locationOwnersReq = this.locationOwnerService.list({
      ordering: 'name',
      ...query
    }).subscribe(locationOwners => {
      this.ownerOptions = locationOwners.map(locationOwner => {
        return { name: locationOwner.name, id: locationOwner.id };
      });
      let ownOrganization = this.authenticationService.getOrganization();
      if (ownOrganization) {
        let _locationOwner = new LocationOwnerSerializer().fromJson({
          organization: ownOrganization.id,
          name: ownOrganization.name
        });
        this.ownerOptions = reject(this.ownerOptions, _locationOwner);
        this.ownerOptions.unshift(_locationOwner);
      }
      if (this.location.locationOwner && this.location.locationOwner.id) {
        this.ownerOptions = reject(this.ownerOptions, this.location.locationOwner);
        this.ownerOptions.unshift(this.location.locationOwner);
        this.ownersDropdown.selectedOption = _find(this.ownerOptions, { id: this.location.locationOwner.id });
      }
      this.ownerOptions.unshift({
        name: 'Add a New Owner',
        id: 'new-location-owner',
        button: true
      });
      if (this.ownersDropdown) {
        this.ownersDropdown.config.loadingOptions = false;
      }
      this.ownersConfig.loadingOptions = false;
    }, err => {
      this.errors = err;
    }, () => {
      this.ownersConfig.loadingOptions = false;
    });
  }

  dropdownNextPage(e, type) {
    let config;
    let service;
    let options;

    switch (type) {
      case 'owner':
        config = this.ownersConfig;
        service = this.locationOwnerService;
        options = this.ownerOptions;
        break;
    }

    if (!config.loadingOptions) {
      let o = service && service.listNext();
      if (o) {
        config.loadingOptions = true;
        o.subscribe(results => {
          switch (type) {
            case 'owner':
              let _ownerOptions = results.map(result => {
                return { name: result.name, id: result.id };
              });
              this.ownerOptions = this.ownerOptions.concat(_ownerOptions);
              break;
          }
          config.loadingOptions = false;
        }, (err) => {
          this.errors = parseErrors(err);
          config.loadingOptions = false;
        });
      }
    }
  }

  dropdownSearch(term = '', type) {
    switch (type) {
      case 'owner':
        this.getLocationOwners({ search: term });
        break;
      default:
        throw 'invalid dropdown type';
    }
  }

  initMap(event: any) {
    this.map = event;
    let latLng;

    if (this.map) {
      let bounds = new google.maps.LatLngBounds();
      if (this.location && this.location.latitude && this.location.longitude) {
        latLng = new google.maps.LatLng(
          parseFloat(this.location.latitude),
          parseFloat(this.location.longitude)
        );
        bounds.extend(latLng);
      }

      if (this.location.paths) {
        this.location.paths.forEach((path) => {
          if (path.lat && path.lng) {
            bounds.extend(new google.maps.LatLng(
              parseFloat(path.lat), parseFloat(path.lng)
            ));
          }
        });
      } else if (typeof this.map.setCenter === 'function' && latLng) {
        this.map.setCenter(latLng);
      }

      if (typeof this.map.fitBounds === 'function') {
        this.map.fitBounds(bounds);
      }
      if (!this.location.paths) {
        let currentZoom = this.map.getZoom();
        this.map.setZoom(currentZoom - 3);
      }

      this.initDrawingManager();
    }
  }

  initDrawingManager() {
    let paths = (this.paths && this.paths.length) ? this.paths : this.location.paths;
    if (this.location && this.location.id && paths) {
      this.polygon = new google.maps.Polygon({
        paths: paths,
        strokeColor: '#FF8C00',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF8C00',
        fillOpacity: 0.35,
        editable: true,
        draggable: true
      });
      if (this.map) { this.polygon.setMap(this.map); }
    }

    if (this.drawingManager) {
      this.allSubscriptionsToUnsubscribe.push(
        this.drawingManager['initialized$'].subscribe(dm => {
          if (!this.polygon) {
            this.polygon = new google.maps.Polygon({
              paths: paths || [],
              strokeColor: '#FF8C00',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillColor: '#FF8C00',
              fillOpacity: 0.35,
              editable: true,
              draggable: true
            });
            if (dm && dm.map) { this.polygon.setMap(dm.map); }
          }
          google.maps.event.addListener(this.polygon, 'insert_at', event => {
            let pathArray = this.polygon.getPath().getArray();
            this.updatePaths(pathArray);
          });

          google.maps.event.addListener(this.polygon, 'set_at', event => {
            let pathArray = this.polygon.getPath().getArray();
            this.updatePaths(pathArray);
          });

          google.maps.event.addListener(this.polygon, 'dragend', event => {
            let pathArray = this.polygon.getPath().getArray();
            this.updatePaths(pathArray);
          });

          google.maps.event.addListener(this.polygon, 'rightclick', event => {
            this.deletePolygon();
          });

          google.maps.event.addListener(dm, 'overlaycomplete', event => {
            dm.map.data.setStyle({
              editable: true,
              draggable: true
            });
            if (event.type !== google.maps.drawing.OverlayType.MARKER) {
              this.drawingComplete(event.overlay);
              dm.setDrawingMode(null);
            }
          });
        })
      );
    }
  }

  submit(): void {
    let coordinates = null;
    if (this.polygon) {
      let polygonPath = this.polygon.getPath();
      if (polygonPath) {
        let pathArray = polygonPath.getArray();
        if (pathArray) {
          coordinates = pathArray.map((path) => {
            return [path.lng(), path.lat()];
          });
          coordinates = uniqWith(coordinates, isEqual);
          if (coordinates && coordinates[0] !== coordinates.reverse[0]) {
            coordinates.push(coordinates[0]);
          }
        }
      }
    }
    this.loading = true;
    if (this.location.geofence && this.location.geofence instanceof Array) {
      let geofence = this.location.geofence[0];
      if (geofence && geofence.geometry) {
        this.location.geofence = geofence.geometry;
      }
    } else if (coordinates) {
      this.location.geofence = {
        type: 'Polygon',
        coordinates: [coordinates]
      };
    }
    if (this.location.tags && this.location.tags.length) {
      this.location.tags = this.location.tags.map(t => (typeof t === 'string' ? t : t.name));
    }
    this.locationService.save(this.location).subscribe((res) => {
      this.loading = false;
      if (this.returnTo.includes('?')) {
        let queryParams = {};
        let splitUrl = this.returnTo.split(/[\\?&]+/);
        splitUrl.forEach(urlPart => {
          if (urlPart.includes('=')) {
            queryParams[urlPart.split('=')[0]] = urlPart.split('=')[1];
          }
        });
        this.router.navigate([splitUrl[0].replace('/', '')], { queryParams: queryParams });
      } else {
        this.router.navigate([this.returnTo]);
      }
    }, (err) => {
      this.errors = parseErrors(err);
      this.loading = false;
    });
  }

  cancel(): void {
    this.loading = false;
    if (this.returnTo.includes('?')) {
      let queryParams = {};
      let splitUrl = this.returnTo.split(/[\\?&]+/);
      splitUrl.forEach(urlPart => {
        if (urlPart.includes('=')) {
          queryParams[urlPart.split('=')[0]] = urlPart.split('=')[1];
        }
      });
      this.router.navigate([splitUrl[0].replace('/', '')], { queryParams: queryParams });
    } else {
      this.router.navigate([this.returnTo]);
    }
  }

  placeChanged(place): void {
    // set latitude, longitude and zoom
    if (place && place.geometry) {
      this.location.latitude = place.geometry.location.lat();
      this.location.longitude = place.geometry.location.lng();
    }
    this.location.marker = {
      latitude: this.location.latitude, longitude: this.location.longitude
    };
    if (this.location.latitude && this.location.longitude) {
      this.marker = null;
      this.marker = {
        latLng: new google.maps.LatLng(
          parseFloat(this.location.latitude), parseFloat(this.location.longitude)
        ),
        mappable: true
      };
    }
    this.zoom = 14;
    this.address.street = null;

    this.location.city = this.extractFromAddress(place.address_components, 'locality');
    this.countryOptions.map(country => {
      if (country.name === this.extractFromAddress(place.address_components, 'country')) {
        this.selectCountry(country.id);
      }
    });
    this.states.map(state => {
      if (state.name === this.extractFromAddress(place.address_components, 'administrative_area_level_1')) {
        this.location.state = state.abbreviation;
      }
    });
    this.location.zipcode = this.extractFromAddress(place.address_components, 'postal_code');
    this.centerOnCoordinates({ latitude: this.location.latitude, longitude: this.location.longitude });
    if (!this.location.name || !this.location.name.length) {
      this.location.name = place.name;
    }
    this.location.street = this.extractFromAddress(place.address_components, 'street_number') + ' ' +
      this.extractFromAddress(place.address_components, 'route');
    if (this.location.street && this.location.street.trim().length === 0) {
      this.location.street = this.location.name;
    }
  }

  centerOnCoordinates(coordinates): void {
    let fallbackCenter = new google.maps.LatLng(
      coordinates.latitude, coordinates.longitude
    );
    this.mapOptions = mapOptions({
      zoom: 15,
      disableDefaultUI: true,
      scrollwheel: true,
      fullscreenControl: false,
      streetViewControl: false,
      center: fallbackCenter,
      geoFallbackCenter: fallbackCenter
    }, {}, {
      mapStyle: 'google-map-style'
    });
    navigator.geolocation.clearWatch(this.geolocationSub);
    this.geolocationSub = null;
    if (this.map) { google.maps.event.trigger(this.map, 'resize'); }
  }

  selectState(state: string): void {
    this.location.state = state;
  }

  selectCountry(country: string): void {
    this.location.country = country;
    this.stateOptions = filter(this.states, { country: country }).map(state => {
      return {
        id: state.abbreviation,
        name: state.name
      };
    });
  }

  selectOwner(owner): void {
    if (owner['id'] === 'new-location-owner') {
      this.showLocationOwnerTextField = true;
      this.location.locationOwner.id = null;
    } else {
      this.location.locationOwner = owner;
    }
  }

  markerDragEnd(event): void {
    this.markerMoved = true;
    if (event.coords) {
      this.markerLocation.latitude = event.coords.lat;
      this.markerLocation.longitude = event.coords.lng;
    } else if (event.latLng) {
      this.markerLocation.latitude = event.latLng.lat();
      this.markerLocation.longitude = event.latLng.lng();
    }
  }

  onPolyInit(polygon): void {
    this.polygon = polygon;
  }

  extractFromAddress(components, type: string, property: string = null): string {
    for (let i = 0; i < components.length; i++) {
      for (let j = 0; j < components[i].types.length; j++) {
        if (components[i].types[j] === type) {
          return property ? components[i][property] : components[i].long_name;
        }
      }
    }
    return '';
  }

  geolocate(): void {
    let displayName = `${this.location.street} ${this.location.city}, ${this.location.state} ${this.location.zipcode}`;
    if (this.location.street && this.location.street.trim().length > 0) {
      displayName = displayName.replace('undefined', '');
      displayName = displayName.replace(' ,', ',');
    }
    this.address = {
      street: this.location.street,
      street2: null,
      city: this.location.city,
      state: this.location.state,
      zipcode: this.location.zipcode,
      country: this.location.country,
      displayName: displayName
    };

    let status = Object.keys(this.address).every((x) => {
      return this.address[x] && this.address[x].length;
    }) === true;
    let address = Object.keys(this.address).map((x) => {
      return this.address[x];
    }).join(', ');
    this.copyUpdatedAddress();

    if (status) {
      this.locationService.getLocationByAddress(address).subscribe((res) => {
        let coordinates = res && res[0];
        this.location.latitude = coordinates.geometry.location.lat;
        this.location.longitude = coordinates.geometry.location.lng;
        this.location.marker = {
          latitude: this.location.latitude, longitude: this.location.longitude
        };
        this.centerOnCoordinates({ latitude: this.location.latitude, longitude: this.location.longitude });
      }, (err) => {
        this.errors = parseErrors(err);
        this.loading = false;
      });
    } else {
      this.centerOnCoordinates({ latitude: this.location.latitude, longitude: this.location.longitude });
    }
  }

  onConfirmNewMarkerLocation(): void {
    this.markerMoved = false;
    this.location.latitude = this.markerLocation.latitude;
    this.location.longitude = this.markerLocation.longitude;
    this.reverseGeocode(this.markerLocation);
    this.copyUpdatedAddress();
  }

  copyUpdatedAddress(): void {
    if (this.address && this.location && this.address.street !== this.location.street) {
      this.selectCountry(this.address.country);
      this.location.street = this.address.street;
      this.location.city = this.address.city;
      this.location.state = this.address.state;
      this.location.zipcode = this.address.zipcode;
      this.location.country = this.address.country;
    }
    this.address = new Address();
  }

  reverseGeocode(coords: Location): void {
    if (coords && coords.latitude && coords.longitude) {
      let coordinates = [coords.latitude, coords.longitude];
      this.locationService.getAddressByCoordinates(coordinates).subscribe((res) => {
        this.addresses = this.processAddresses(res);
        this.selectedAddress = this.addresses[0];
        this.address = this.addresses[0];
        if (!this.location.street) { this.copyUpdatedAddress(); }
      }, (err) => {
        this.errors = parseErrors(err);
        this.loading = false;
      });
    }
  }

  processAddresses(results): Address[] {
    let addresses: Address[] = [];

    results.forEach(result => {
      let address: Address = new Address();
      address.street = this.extractFromAddress(result.address_components, 'street_number')
        + ' '
        + this.extractFromAddress(result.address_components, 'route');
      address.city = this.extractFromAddress(result.address_components, 'locality');
      this.apiService.getStates().map((state) => {
        if (state.name === this.extractFromAddress(result.address_components, 'administrative_area_level_1') ||
        state.abbreviation === this.extractFromAddress(result.address_components, 'administrative_area_level_1', 'short_name')) {
          address.state = state.abbreviation;
        }
      });
      address.zipcode = this.extractFromAddress(result.address_components, 'postal_code');

      this.countryOptions.map(country => {
        if (country.name === this.extractFromAddress(result.address_components, 'country')) {
          address.country = country.id;
        }
      });

      address.displayName = `${address.street} ${address.street2}, ${address.city}, ${address.state} ${address.zipcode}`;
      if (address.street && address.street.trim().length > 0) {
        address.displayName = address.displayName.replace('undefined', '');
        address.displayName = address.displayName.replace(' ,', ',');
      }

      addresses.push(address);
    });

    addresses.sort(function (a, b) {
      const streetA = a.street && a.street.trim();
      const streetB = b.street && b.street.trim();
      if ((streetA && streetA.length > 0) && (!streetB || streetB.length === 0)) {
        return -1;
      }
      if ((!streetA || streetA.length === 0) && (streetB && streetB.length > 0)) {
        return 1;
      }

      const stateA = a.state && a.state.trim();
      const stateB = b.state && b.state.trim();
      if ((stateA && stateA.length > 0) && (!stateB || stateB.length === 0)) {
        return -1;
      }
      if ((!stateA || stateA.length === 0) && (stateB && stateB.length > 0)) {
        return 1;
      }

      return 0;
    });

    return addresses;
  }

  updateAddress(address: Address): void {
    if (address && this.location) {
      this.location.street = address.street;
      this.location.city = address.city;
      this.location.state = address.state;
      this.location.zipcode = address.zipcode;
      this.location.country = address.country;
    }
  }

  drawingComplete(overlay): void {
    let map = overlay.map;
    console.log('drawingComplete');
    map.data.add(new google.maps.Data.Feature({
      geometry: new google.maps.Data.Polygon([overlay.getPath().getArray()])
    }));
    this.isGeoFenceAvailable = true;
    overlay.setMap(null); // Remove the original overlay from the map
    map.data.toGeoJson((json) => { this.location.geofence = json.features; });
    map.data.forEach((feature) => {
      map.data.overrideStyle(feature, {
        strokeColor: '#FF8C00',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF8C00',
        fillOpacity: 0.35
      });
    });

    map.data.addListener('click', event => {
      this.setSelectedOverlay(map, event.feature);
    });

    map.data.addListener('rightclick', event => {
      this.deleteSelectedOverlay(map, event.feature);
    });

    map.data.addListener('setgeometry', event => {
      this.setSelectedOverlay(map, event.feature);
      console.log('setgeometry');
      map.data.toGeoJson((json) => { this.location.geofence = json.features; });
    });
  }

  setSelectedOverlay(map, feature): void {
    map.data.revertStyle();
    this.selectedOverlay = feature;
    map.data.overrideStyle(this.selectedOverlay, {
      fillColor: '#1D68D1'
    });
  }

  deleteSelectedOverlay(map, overlay = null): void {
    this.isGeoFenceAvailable = false;
    if (overlay) { this.selectedOverlay = overlay; }
    if (this.selectedOverlay) {
      map.data.remove(this.selectedOverlay);
      map.data.toGeoJson((json) => { this.location.geofence = json.features; });
      delete this.selectedOverlay;
    }
  }

  deletePolygon(polygon = null): void {
    if (polygon) { this.polygon = polygon; }
    if (this.polygon) {
      this.polygon.setMap(null);
      this.location.geofence = null;
      delete this.polygon;
    }
  }

  updatePaths(pathArray): void {
    // let pathArray = event.target.getPath().getArray();
    this.paths = pathArray.map((path) => {
      return { lat: path.lat(), lng: path.lng() };
    });
    if (this.paths[0] === this.paths.reverse[0]) {
      console.log('Valid GeoJSON');
    } else {
      console.log('Invalid GeoJSON!');
    }
  }

  onSelect(propertyName: string, e): void {
    this.location[propertyName] = e;
  }

  clearGeoFence() {
    if (this.map) {
      if (this.polygon) {
        this.polygon.setMap(null);
        this.polygon = null;
      }
      this.map.data.forEach((feature) => {
        this.map.data.remove(feature);
      });
    }
    this.location.geofence = null;
    this.isGeoFenceAvailable = false;
  }
}
